// src/pages/RoleManagement/modals/CreateRoleModal.tsx
// Create new role group modal with Formik validation

import React from 'react';
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Form,
  FormGroup,
  Label,
  Input,
  FormFeedback,
} from 'reactstrap';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';

// Components
import ColorPicker from '../components/ColorPicker';
import ModuleCheckboxList from '../components/ModuleCheckboxList';

// Redux
import { closeModal } from 'slices/roleManagement/reducer';
import { createNewRoleGroup } from 'slices/roleManagement/thunk';

// Types
import { Module } from '../types';
import { PageActionPermission } from 'services/pageActionPermission/PageActionPermission';
import { createScreenText } from 'helpers/screenText';
const st = createScreenText('role_management');

interface CreateRoleModalProps {
  isOpen: boolean;
  toggle: () => void;
  modules: Module[];
  onSuccess?: () => void;
}

const CreateRoleModal: React.FC<CreateRoleModalProps> = ({
  isOpen,
  toggle,
  modules,
  onSuccess,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch<any>();
  const location = useLocation();

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      code: '',
      name: '',
      description: '',
      color: '',
      moduleIds: [] as number[],
    },
    validationSchema: Yup.object({
      code: Yup.string()
        .required(st('ma_nhom_quyen_khong_duoc_de_error'))
        .min(3, st('ma_phai_co_it_nhat_3_error'))
        .matches(/^[A-Z_]+$/, st('ma_chi_duoc_chua_chu_in_error')),
      name: Yup.string()
        .required(st('ten_nhom_quyen_khong_duoc_de_error'))
        .min(3, st('ten_phai_co_it_nhat_3_error')),
      description: Yup.string()
        .required(st('vui_long_nhap_mo_ta_error'))
        .min(10, st('mo_ta_phai_co_it_nhat_error')),
      color: Yup.string().required(st('vui_long_chon_mot_mau_error')),
      moduleIds: Yup.array()
        .min(1, st('vui_long_chon_it_nhat_mot_error'))
        .required(st('vui_long_chon_quyen_error')),
    }),
    onSubmit: async (values) => {
      try {
        await dispatch(
          createNewRoleGroup({
            code: values.code ?? '',
            name: values.name,
            description: values.description,
            color: values.color,
            isDefault: false,
            moduleIds: values.moduleIds,
            permissionIds: []
          })
        ).unwrap();
        toast.success(st('tao_nhom_quyen_thanh_cong_message'));
        validation.resetForm();
        // Backend CreateRole chỉ trả về long (id), không đủ dữ liệu để patch state.roleGroups —
        // phải load lại danh sách thì mới thấy role mới ngay, không cần F5.
        onSuccess?.();
      } catch (error: any) {
        toast.error(error || st('co_loi_xay_ra_khi_tao_message'));
      }
    },
  });
  
  const handleClose = () => {
    validation.resetForm();
    toggle();
  };
  
  return (
    <Modal isOpen={isOpen} toggle={handleClose} centered size="lg">
      <ModalHeader toggle={handleClose}>
        {st('tao_nhom_quyen_moi_title')}
      </ModalHeader>
      <Form onSubmit={validation.handleSubmit}>
        <ModalBody>
          {/* Code */}
          <FormGroup>
            <Label for="code">{st('ma_label')}</Label>
            <Input
              type="text"
              id="code"
              name="code"
              placeholder={st('vd_manager_operator_placeholder')}
              value={validation.values.code}
              data-action-code="create-input-code"
              data-action-name="Code nhóm quyền"
              onChange={validation.handleChange}
              onBlur={validation.handleBlur}
              disabled={!PageActionPermission.canEnable(location.pathname, "create-input-code")}
              invalid={
                validation.touched.code && validation.errors.code
                  ? true
                  : false
              }
            />
            {validation.touched.code && validation.errors.code ? (
              <FormFeedback type="invalid">
                {validation.errors.code}
              </FormFeedback>
            ) : null}
            <small className="text-muted">
              {st('chi_dung_chu_in_hoa_va_hint')}
            </small>
          </FormGroup>
          
          {/* Name */}
          <FormGroup>
            <Label for="name">{st('ten_label')}</Label>
            <Input
              type="text"
              id="name"
              name="name"
              placeholder={st('nhap_ten_nhom_quyen_placeholder')}
              value={validation.values.name}
              data-action-code="create-input-name"
              data-action-name="Tên nhóm quyền"
              onChange={validation.handleChange}
              onBlur={validation.handleBlur}
              disabled={!PageActionPermission.canEnable(location.pathname, "create-input-name")}
              invalid={
                validation.touched.name && validation.errors.name
                  ? true
                  : false
              }
            />
            {validation.touched.name && validation.errors.name ? (
              <FormFeedback type="invalid">
                {validation.errors.name}
              </FormFeedback>
            ) : null}
          </FormGroup>
          
          {/* Description */}
          <FormGroup>
            <Label for="description">{st('mo_ta_label')}</Label>
            <Input
              type="textarea"
              id="description"
              name="description"
              placeholder={st('mo_ta_nhom_quyen_va_trach_placeholder')}
              rows={3}
              value={validation.values.description}
              data-action-code="create-input-description"
              data-action-name="Mô tả nhóm quyền"
              onChange={validation.handleChange}
              onBlur={validation.handleBlur}
              disabled={!PageActionPermission.canEnable(location.pathname, "create-input-description")}
              invalid={
                validation.touched.description && validation.errors.description
                  ? true
                  : false
              }
            />
            {validation.touched.description && validation.errors.description ? (
              <FormFeedback type="invalid">
                {validation.errors.description}
              </FormFeedback>
            ) : null}
          </FormGroup>
          
          {/* Color Picker */}
          <FormGroup>
            <ColorPicker
              value={validation.values.color}
              onChange={(color) => validation.setFieldValue('color', color)}
            />
            {validation.touched.color && validation.errors.color ? (
              <div className="text-danger mt-1">
                <small>{validation.errors.color}</small>
              </div>
            ) : null}
          </FormGroup>
          
          <hr />
          
          {/* Module Selection */}
          <FormGroup>
            <ModuleCheckboxList
              modules={modules}
              selectedModuleIds={validation.values.moduleIds}
              onChange={(moduleIds) => validation.setFieldValue('moduleIds', moduleIds)}
            />
            {validation.touched.moduleIds && validation.errors.moduleIds ? (
              <div className="text-danger mt-2">
                <small>{validation.errors.moduleIds}</small>
              </div>
            ) : null}
          </FormGroup>
        </ModalBody>
        <ModalFooter>
          <Button color="light" onClick={handleClose}>
            {st('huy_button')}
          </Button>
          {PageActionPermission.canDisplay(location.pathname, "button-create-submit") && (
            <Button
              color="primary"
              type="submit"
              disabled={validation.isSubmitting}
              data-action-code="button-create-submit"
              data-action-name="Tạo nhóm quyền"
            >
              <i className="ri-save-line me-1"></i>
              {validation.isSubmitting ? st('dang_tao_text') : st('tao_nhom_quyen_text')}
            </Button>
          )}
        </ModalFooter>
      </Form>
    </Modal>
  );
};

export default CreateRoleModal;